import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useRealtimeRace, useRealtimeParticipants, useRealtimeLaps } from '../hooks/useRealtime'
import { LeaderboardGrid } from '../components/leaderboard/LeaderboardGrid'
import { BestLapBanner } from '../components/leaderboard/BestLapBanner'
import { Timer } from '../components/ui/Timer'
import type { RaceParticipant } from '../types'

export function LeaderboardPage() {
  const [searchParams] = useSearchParams()
  const raceId = searchParams.get('race') || ''
  const [bestLapParticipant, setBestLapParticipant] = useState<RaceParticipant | null>(null)
  const [showBestLapBanner, setShowBestLapBanner] = useState(false)
  const [previousBestLap, setPreviousBestLap] = useState<number | null>(null)

  const { race } = useRealtimeRace(raceId)
  const { participants } = useRealtimeParticipants(raceId)
  const { laps } = useRealtimeLaps(raceId)

  // Detect new best lap
  useEffect(() => {
    if (participants.length === 0) return

    const bestLap = Math.min(...participants.map(p => p.best_lap_time || Infinity).filter(t => t !== Infinity))

    if (bestLap === Infinity) return
    if (previousBestLap === null) {
      setPreviousBestLap(bestLap)
      return
    }

    if (bestLap < previousBestLap) {
      const participant = participants.find(p => p.best_lap_time === bestLap)
      if (participant) {
        setBestLapParticipant(participant)
        setShowBestLapBanner(true)
        setPreviousBestLap(bestLap)
      }
    }
  }, [participants, previousBestLap])

  const elapsed = race?.started_at
    ? new Date().getTime() - new Date(race.started_at).getTime()
    : 0

  return (
    <div className="w-full h-screen bg-gradient-to-br from-palm-dark via-[#0f1419] to-palm-dark flex flex-col overflow-hidden">
      {/* Best Lap Banner */}
      {bestLapParticipant && (
        <BestLapBanner
          pilotName={bestLapParticipant.pilot_name}
          boatNumber={bestLapParticipant.boat_number}
          boatColor={bestLapParticipant.boat_color}
          lapTime={bestLapParticipant.best_lap_time || 0}
          isVisible={showBestLapBanner}
        />
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-shrink-0 p-8 border-b border-tropical-green border-opacity-30 backdrop-blur-sm"
      >
        <div className="flex items-center justify-between gap-8">
          <div>
            <h1 className="text-5xl font-fredoka font-700 text-sand-light mb-2">
              {race?.name || 'Race Leaderboard'}
            </h1>
            <p className="text-tropical-green text-lg font-quicksand">
              Live Race Updates
            </p>
          </div>

          {/* Timer */}
          <motion.div
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <div className="text-sm uppercase text-gray-400 font-quicksand">
              Race Time
            </div>
            <Timer
              elapsed={elapsed}
              isActive={race?.status === 'active'}
              size="lg"
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Leaderboard */}
      <div className="flex-1 overflow-auto p-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1 }}
        >
          {participants.length > 0 ? (
            <LeaderboardGrid
              participants={participants}
              newRecordId={bestLapParticipant?.id}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400 font-quicksand">
                <p className="text-xl mb-2">No participants yet</p>
                <p className="text-sm">Waiting for race to start...</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-shrink-0 p-4 border-t border-tropical-green border-opacity-30 backdrop-blur-sm text-center text-xs text-gray-400 font-quicksand"
      >
        Aqua-Rush - Race Control | {participants.length} Participants | {laps.length} Laps
      </motion.div>
    </div>
  )
}
